import { useState, useEffect } from 'react';
import { Check, X, RefreshCw, Eye, Timer as TimerIcon, ArrowLeft, ArrowRight, Sparkles } from 'lucide-react';
import { launchConfetti, showAchievement } from '../utils/effects.js';
import './Exercise.css';
import './ErrorDetectionExercise.css';

function ErrorDetectionExercise({ exerciseData, onCorrectAnswer }) {
  const { title, subtitle, sentences = [] } = exerciseData || {};

  const [currentIndex, setCurrentIndex] = useState(0);
  const [userChoices, setUserChoices] = useState(Array(sentences.length).fill(null));
  const [answeredCount, setAnsweredCount] = useState(0);
  const [correctCount, setCorrectCount] = useState(0);
  const [timeLeft, setTimeLeft] = useState(180);

  const restartExercise = () => {
    setCurrentIndex(0);
    setUserChoices(Array(sentences.length).fill(null));
    setAnsweredCount(0);
    setCorrectCount(0);
    setTimeLeft(180);
  };

  useEffect(() => {
    if (answeredCount === sentences.length) return; // Detener el timer si todo está respondido
    if (timeLeft <= 0) return;
    const timer = setInterval(() => setTimeLeft(t => t - 1), 1000);
    return () => clearInterval(timer);
  }, [timeLeft, answeredCount, sentences.length]);
  
  const currentSentence = sentences[currentIndex];
  const isAnswered = userChoices[currentIndex] !== null;
  const isChoiceCorrect = isAnswered && userChoices[currentIndex] === currentSentence?.answer;

  const handleAnswer = (answer) => {
    if (isAnswered) return;
    
    const newUserChoices = [...userChoices];
    newUserChoices[currentIndex] = answer;
    setUserChoices(newUserChoices);
    
    setAnsweredCount(prev => prev + 1);
    if (answer === currentSentence?.answer) {
      setCorrectCount(prev => prev + 1);
    }
  };

  const handleNext = () => {
    if (currentIndex < sentences.length - 1) {
      setCurrentIndex(prev => prev + 1);
    } else {
      onCorrectAnswer();
      showAchievement('¡Caza de errores completada!', '🕵️‍♂️');
      launchConfetti();
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev + 1);
    }
  };

  if (!sentences || sentences.length === 0) {
    return <div className="exercise-container">Cargando oraciones...</div>;
  }
  
  const precision = answeredCount > 0 ? Math.round((correctCount / answeredCount) * 100) : 0;
  const progressPercentage = sentences.length > 0 ? ((answeredCount / sentences.length) * 100) : 0;

  return (
    <div className="exercise-container">
      <header className="ede-header">
        <div className="ede-title-group">
          <div className="ede-icon-container-inset"><TimerIcon size={24} /></div>
          <div>
            <h2 className="main-title">{title || '¡Cazando Errores!'}</h2>
            <p className="main-subtitle">{subtitle || 'Analiza cada oración y descubre la verdad.'}</p>
          </div>
        </div>
        <div className="ede-controls">
          <div className="ede-stat-box-inset"><TimerIcon size={18} /> {Math.floor(timeLeft/60)}:{(timeLeft%60).toString().padStart(2, '0')}</div>
          <button className="ede-control-btn"><Eye size={18} /></button>
          <button className="ede-control-btn" onClick={restartExercise}><RefreshCw size={18} /></button>
        </div>
      </header>

      <div className="ede-progress-bar-container">
        <div className="ede-progress-bar-fill" style={{ width: `${progressPercentage}%` }}></div>
      </div>
      <div className="ede-stats-container">
        <div className="ede-stat-box-inset large">
          <div className="ede-stat-number">{currentIndex + 1}/{sentences.length}</div>
          <div className="ede-stat-label">Pregunta</div>
        </div>
        <div className="ede-stat-box-inset large">
          <div className="ede-stat-number">{answeredCount}/{sentences.length}</div>
          <div className="ede-stat-label">Respondidas</div>
        </div>
        <div className="ede-stat-box-inset large">
          <div className="ede-stat-number">{precision}%</div>
          <div className="ede-stat-label">Precisión</div>
        </div>
      </div>

      <main className="ede-card">
        <div className="ede-card-header">
            <Sparkles size={24} className="main-icon" />
            <h3 className="ede-content-title">Detecta el Error Gramatical</h3>
        </div>
        <p className="ede-content-instruction">{currentSentence?.instruction || 'Lee la oración. ¿Es correcta o tiene un error?'}</p>
        
        <div className="ede-sentence-to-evaluate">
          "{currentSentence?.sentence}"
        </div>

        <div className="ede-answer-buttons">
          <button 
            className={`ede-choice-button correct ${userChoices[currentIndex] === 'Correct' ? 'selected' : ''}`}
            onClick={() => handleAnswer('Correct')}
            disabled={isAnswered}
          >
            <Check size={20} /> Correct
          </button>
          <button 
            className={`ede-choice-button incorrect ${userChoices[currentIndex] === 'Incorrect' ? 'selected' : ''}`}
            onClick={() => handleAnswer('Incorrect')}
            disabled={isAnswered}
          >
            <X size={20} /> Incorrect
          </button>
        </div>

        {isAnswered && (
          <div className={`ede-feedback-box ${isChoiceCorrect ? 'correct' : 'incorrect'}`}>
            <div className="ede-feedback-title">
              {isChoiceCorrect ? <Check size={18}/> : <X size={18}/>}
              {isChoiceCorrect ? '¡Excelente trabajo!' : 'Respuesta incorrecta'}
            </div>
            <p className="ede-feedback-explanation">{currentSentence?.explanation}</p>
          </div>
        )}
      </main>

      <footer className="ede-navigation-controls">
         <button className="exercise-action-button" onClick={handlePrevious} disabled={currentIndex === 0}>
           <ArrowLeft size={20} /> Back
         </button>
         <div className="ede-progress-dots">
            {sentences.map((_, index) => (
              <div key={index} className={`ede-dot ${index === currentIndex ? 'active' : ''} ${userChoices[index] !== null ? 'answered' : ''}`}></div>
            ))}
         </div>
         <button className="exercise-action-button" onClick={handleNext} disabled={!isAnswered}>
            {currentIndex === sentences.length - 1 ? 'Finish' : 'Next'} <ArrowRight size={20} />
         </button>
      </footer>
    </div>
  );
}

export default ErrorDetectionExercise;